import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonStyles from "@components/CommonStyles";
import administrativeService from "@base/modules/administrative/administrativeService";
import { IProvince, IWard } from "@base/modules/administrative/administrative.interface";

export interface IServiceAddress {
  provinceCode?: string;
  provinceName?: string;
  wardCode?: string;
  wardName?: string;
  street?: string;
}

interface IProps {
  label: string;
  required?: boolean;
  /** Only the street line is optional for some packages; province + ward always come as a pair. */
  withStreet?: boolean;
  /** Where there is room (a dialog), each field carries its own title instead of one shared label. */
  labeledFields?: boolean;
  value: IServiceAddress;
  error?: string;
  onChange: (value: IServiceAddress) => void;
}

/** Renders the address as province + ward + street: since the 2025 merge there is no district. */
export default function ServiceAddressFields({ label, required, withStreet = true, labeledFields, value, error, onChange }: IProps) {
  //! state
  const { t } = useTranslation();
  const [provinces, setProvinces] = useState<IProvince[]>([]);
  const [wards, setWards] = useState<IWard[]>([]);

  //! function
  useEffect(() => {
    (async () => {
      try {
        const resp = await administrativeService.getAllProvinceSorted();
        setProvinces(resp.data ?? []);
      } catch (error) {}
    })();
  }, []);

  useEffect(() => {
    if (!value.provinceCode) {
      setWards([]);
      return;
    }

    (async () => {
      try {
        const resp = await administrativeService.getListWardByProvinceCode(value.provinceCode);
        setWards(resp.data ?? []);
      } catch (error) {}
    })();
  }, [value.provinceCode]);

  const onChangeProvince = (provinceCode: string) => {
    const province = provinces.find((item) => item.code === provinceCode);

    // The ward list belongs to the old province, so it cannot survive the change.
    onChange({ ...value, provinceCode, provinceName: province?.name, wardCode: undefined, wardName: undefined });
  };

  const onChangeWard = (wardCode: string) => {
    const ward = wards.find((item) => item.code === wardCode);

    onChange({ ...value, wardCode, wardName: ward?.name });
  };

  //! render
  return (
    <div className="flex flex-col gap-2">
      {labeledFields ? null : (
        <span className="text-common-maintext text-sm font-medium font-['Inter']">
          {label} {required ? <span className="text-common-red500">*</span> : null}
        </span>
      )}

      <div className={`grid grid-cols-1 gap-2 ${labeledFields ? "" : "sm:grid-cols-2"}`}>
        <CommonStyles.SelectUI
          label={labeledFields ? t("user.profile.handler.labelProvince") : undefined}
          required={required}
          variant="filled"
          showSearch
          optionFilterProp="label"
          value={value.provinceCode}
          placeholder={t("Partner.Form.placeholderProvince")}
          options={provinces.map((province) => ({ label: province.name, value: province.code }))}
          onChange={onChangeProvince}
        />

        <CommonStyles.SelectUI
          variant="filled"
          label={labeledFields ? t("user.profile.handler.labelDistrict") : undefined}
          required={required}
          showSearch
          optionFilterProp="label"
          disabled={!value.provinceCode}
          value={value.wardCode}
          placeholder={t("user.profile.syncFromSite.placeHolderWard")}
          options={wards.map((ward) => ({ label: ward.name, value: ward.code }))}
          onChange={onChangeWard}
        />
      </div>

      {withStreet ? (
        <CommonStyles.InputUI
          label={labeledFields ? t("user.profile.handler.labelAddress") : undefined}
          required={required}
          variant="filled"
          value={value.street}
          placeholder={t("packageBooking.streetPlaceholder")}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, street: event.target.value })}
        />
      ) : null}

      {error ? <span className="text-common-red500 text-xs font-normal font-['Inter']">{error}</span> : null}
    </div>
  );
}
